import {
  BodyText,
  DetailText,
  Flex,
  Modal,
  ModalContent,
  ModalRoot,
} from '@utilitywarehouse/hearth-react';

import { useSyncState } from '@hooks/useSyncState';
import { BroadbandQuote } from '@src/types/broadband';
import { formatCurrency } from '@utils/format';
import { getQuoteMonthlyCharge, getQuoteMonthlyPrice } from '@utils/price';

import { BroadbandBreakdown } from '../BroadbandBreakdown';
import { BroadbandTile } from '../BroadbandTile';
import { BroadbandTileDiscount } from '../types';
import { activeDiscounts, getDiscountedPrice } from './getDiscountedPrice';
import './styles.scss';

interface TariffProps {
  quote: BroadbandQuote;
  selected: boolean;
  value: string;
}

const Tariff: React.FC<TariffProps> = ({ value, selected, quote }) => {
  const [breakdownOpen, setBreakdownOpen] = useSyncState(
    `${quote.id}-breakdown-opened`,
    false
  );
  const price = getQuoteMonthlyPrice(quote);
  const charge = getQuoteMonthlyCharge(quote);
  const discountedPrice = getDiscountedPrice(quote);
  const hasDiscounts = quote.discounts.some((discount) => discount.qualified);
  const discountDuration =
    quote.discounts.find(
      (discount) => discount.qualified && activeDiscounts.includes(discount.id)
    )?.creditFrequency ?? 0;
  const contractLength = quote.product.details.contractLength ?? 0;
  const discount: BroadbandTileDiscount | undefined = hasDiscounts
    ? {
        duration: discountDuration,
        price: `£${discountedPrice.value}`,
        contractLength,
      }
    : undefined;
  const formatIncrease = (increase: number) =>
    formatCurrency({
      ...charge?.amount,
      value: Number(charge?.amount.value) + increase,
    }).replace(/\.00$/, '');
  return (
    <ModalRoot open={breakdownOpen} onOpenChange={setBreakdownOpen}>
      <BroadbandTile
        contractLength={contractLength}
        breakdown={{
          variant: 'button',
          label: 'More details',
          onClick: () => setBreakdownOpen(true),
        }}
        discount={discount}
        isSelected={selected}
        description={
          <Flex direction="column" spacing="lg">
            {/*
             *  TODO: remove this hardcoding once we have any form of
             *  BE supplied in-contract price increase data
             */}
            <BodyText>
              Price will increase to {formatIncrease(4)} on 1 April 2027 and{' '}
              {formatIncrease(8)} on 1 April 2028
              {hasDiscounts &&
                ', but your free months stay free, whenever you join'}
              .
            </BodyText>
            <BodyText color="primary" as="span">
              <DetailText as="span" size="2xl">
                {quote.product.details.speeds?.[0]?.downstreamAverage}
              </DetailText>{' '}
              Mbps average download speed
            </BodyText>
          </Flex>
        }
        price={price}
        serviceName={quote.product.name}
        testId="broadband-tile"
        value={value}
      />
      <Modal loading={false} heading="Speed information">
        <ModalContent>
          <BroadbandBreakdown product={quote.product} />
        </ModalContent>
      </Modal>
    </ModalRoot>
  );
};

export { Tariff };
